// Finance › Withdraws (wave 3, W2_Portal_Tables look): drivers' requests to
// cash out their earnings, in the modern table with status chips (and their
// counts) above it, and the four money dialogs (Complete, Cancel, Move to
// top-up, Reject) drawn like the approved boards: the request as label /
// value rows, the warning in words, one label above each field, the error
// under the field.
//
// Data and money, exactly as before (no new reads):
// - the list is the withdrawdata slice loaded at sign-in (fetchWithdraws);
//   the status filter runs in memory with the same rules.
// - Complete calls completeWithdraw({ ...request, mobile, processedBy,
//   referenceCode, paymentSource, otherSource, processDate, status:
//   'completed' }); Cancel calls reverseWithdraw(request, reason, by); Move to
//   top-up calls transferWithdrawToTopUp(request, by, note); Reject calls
//   rejectWithdraw(request, reason, by). Each dialog closes at once and the
//   write runs in the background; a failure shows the error and refetches
//   the list (fetchWithdraws), as before. `request` is the plain record plus
//   the driver's mobile from usersdata.users (material-table's own tableData
//   bookkeeping is no longer written into /withdraws).
// - Only admin and finance process requests (the others only view); the CSV
//   needs critical edits on (no print, as before); the driver's phone and
//   bank account number are hidden in demo mode; the bank columns follow
//   settings.bank_fields.
// Call-centre feedback (29 Oct 2026): in the dialogs a copy button beside the
// driver's number copies it without spaces (+251964540448) for MicroSIP; it
// is not there while numbers are hidden.
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { api } from 'common';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import UndoRoundedIcon from '@mui/icons-material/UndoRounded';
import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined';
import { tones } from '../theme';
import { showToast, InlineMessage } from '../components/feedback';
import { formatAddisDateTime } from '../common/exportUtils';
import ClickableUserLink from '../components/ClickableUserLink';
import {
  DataTable,
  ChipGroup,
  CopyButton,
  StatusChip,
  InfoRow,
  FieldLabel,
  FieldError,
  SrOnly,
  calmCaps,
  formatMoney,
  formatPhoneDisplay,
} from '../components/modern';

// The status filter, with the same rules as before.
const FILTERS = {
  pending: (item) => !item.processed && !item.reversed && !item.rejected && item.status !== 'cancelled' && item.status !== 'rejected',
  cancelled: (item) => item.reversed || item.status === 'cancelled',
  rejected: (item) => item.rejected || item.status === 'rejected',
  processed: (item) => item.processed && !item.transferredToTopUp,
  transferred: (item) => !!item.transferredToTopUp,
  all: () => true,
};
const FILTER_ORDER = ['pending', 'cancelled', 'rejected', 'processed', 'transferred', 'all'];

// A request staff can still act on (the old actions were disabled otherwise).
const isOpen = (row) => !(row && (row.reversed || row.processed || row.rejected));

const PAYMENT_SOURCES = ['Chapa', 'SantimPay', 'Other'];

// One money dialog: the title with a close button, the content, the buttons.
// Full screen on a phone so the keyboard never covers the field.
function MoneyDialog({ open, onClose, title, closeLabel, fullScreen, children, actions }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={fullScreen} aria-labelledby="withdraw-dialog-title">
      <DialogTitle id="withdraw-dialog-title" sx={{ pr: 8 }}>
        {title}
        <IconButton aria-label={closeLabel} onClick={onClose} sx={{ position: 'absolute', right: 12, top: 12 }}>
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: '8px !important' }}>{children}</DialogContent>
      <DialogActions>{actions}</DialogActions>
    </Dialog>
  );
}

const Withdraws = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('sm'));
  const settings = useSelector((state) => state.settingsdata.settings);
  const { completeWithdraw, reverseWithdraw, transferWithdrawToTopUp, rejectWithdraw, fetchWithdraws } = api;
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const usersdata = useSelector((state) => state.usersdata);
  const withdrawdata = useSelector((state) => state.withdrawdata);

  const canProcessWithdrawals = auth?.profile?.usertype === 'admin' || auth?.profile?.usertype === 'finance';
  const allowPrivate = !!settings.AllowCriticalEditsAdmin;
  const showBank = settings.bank_fields !== false;

  const [statusFilter, setStatusFilter] = useState('pending'); // Default to pending

  // Complete (and the read-only details of a finished request)
  const [selectedWithdraw, setSelectedWithdraw] = useState(null);
  const [referenceCode, setReferenceCode] = useState('');
  const [paymentSource, setPaymentSource] = useState('');
  const [otherSource, setOtherSource] = useState('');
  // Cancel (reverse), move to top-up, reject
  const [selectedReverseWithdraw, setSelectedReverseWithdraw] = useState(null);
  const [reverseReason, setReverseReason] = useState('');
  const [selectedTransferWithdraw, setSelectedTransferWithdraw] = useState(null);
  const [transferNote, setTransferNote] = useState('');
  const [selectedRejectWithdraw, setSelectedRejectWithdraw] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  // Errors under the fields, shown once Save is pressed.
  const [errors, setErrors] = useState({});

  const L = (key, dflt, opts) => t(key, { defaultValue: dflt, ...(opts || {}) });
  const hiddenText = t('hidden_demo');
  const cancelText = L('confirm_cancel', 'Cancel');
  const closeLabel = L('close_dialog', 'Close');
  const money = (amount) => formatMoney(amount || 0, settings, true);
  const operator = () => (auth.profile && auth.profile.email ? auth.profile.email : auth.profile.uid);

  // ── Rows ─────────────────────────────────────────────────────────────
  const all = useMemo(() => (Array.isArray(withdrawdata.withdraws) ? withdrawdata.withdraws : []), [withdrawdata.withdraws]);
  const counts = useMemo(() => {
    const out = {};
    FILTER_ORDER.forEach((key) => {
      out[key] = all.filter(FILTERS[key]).length;
    });
    return out;
  }, [all]);
  const data = useMemo(() => all.filter(FILTERS[statusFilter] || FILTERS.all), [all, statusFilter]);

  // A dialog opens or closes: it starts without errors.
  useEffect(() => {
    setErrors({});
  }, [selectedWithdraw, selectedReverseWithdraw, selectedTransferWithdraw, selectedRejectWithdraw]);

  const statusOf = (row) => {
    if (row.rejected) return { tone: 'error', icon: <BlockRoundedIcon />, label: t('rejected') };
    if (row.reversed) return { tone: 'error', icon: <UndoRoundedIcon />, label: t('cancelled') };
    if (row.transferredToTopUp) return { tone: 'info', icon: <AccountBalanceWalletOutlinedIcon />, label: t('transferred_to_topup') };
    if (row.processed) return { tone: 'success', icon: <CheckRoundedIcon />, label: t('processed') };
    return { tone: 'warning', icon: <ScheduleRoundedIcon />, label: t('pending') };
  };

  // The driver's mobile from the users list already loaded (as before).
  const withMobile = (rowData) => {
    const withdrawWithMobile = { ...rowData };
    if (usersdata && usersdata.users && rowData.uid) {
      const driver = usersdata.users.find((user) => user.id === rowData.uid);
      if (driver && driver.mobile) {
        withdrawWithMobile.mobile = driver.mobile;
      }
    }
    return withdrawWithMobile;
  };

  // ── Complete ─────────────────────────────────────────────────────────
  const handleProcessWithdraw = (rowData) => {
    const withdrawWithMobile = withMobile(rowData);
    setSelectedWithdraw(withdrawWithMobile);
    setReferenceCode(withdrawWithMobile.referenceCode || '');
    setPaymentSource(withdrawWithMobile.paymentSource || '');
    setOtherSource(withdrawWithMobile.otherSource || '');
  };

  const handleCloseDialog = () => {
    setSelectedWithdraw(null);
    setReferenceCode('');
    setPaymentSource('');
    setOtherSource('');
  };

  const handleConfirmProcess = () => {
    const next = {};
    if (!paymentSource) next.paymentSource = t('payment_source_required');
    else if (paymentSource === 'Other' && !otherSource.trim()) next.otherSource = t('other_source_required');
    if (!referenceCode.trim()) next.referenceCode = t('reference_code_required');
    if (Object.keys(next).length) {
      setErrors(next);
      return;
    }

    // Update withdrawal with reference code, payment source, and status
    const updatedWithdraw = {
      ...selectedWithdraw,
      processedBy: operator(),
      referenceCode: referenceCode.trim(),
      paymentSource: paymentSource === 'Other' ? otherSource.trim() : paymentSource,
      otherSource: paymentSource === 'Other' ? otherSource.trim() : '',
      processDate: new Date().getTime(),
      status: 'completed',
    };

    // Process the withdrawal in the background
    dispatch(completeWithdraw(updatedWithdraw)).then(
      () => showToast.success(L('withdrawal_completed_successfully', 'Withdrawal marked as completed')),
      (error) => {
        showToast.error(t('error_processing_withdrawal') + ': ' + error.message);
        dispatch(fetchWithdraws());
      }
    );

    handleCloseDialog();
  };

  // ── Cancel (the amount goes back to the earning wallet) ──────────────
  const handleReverseWithdraw = (rowData) => {
    setSelectedReverseWithdraw(withMobile(rowData));
    setReverseReason('');
  };

  const handleCloseReverseDialog = () => {
    setSelectedReverseWithdraw(null);
    setReverseReason('');
  };

  const handleConfirmReverse = () => {
    if (!reverseReason.trim()) {
      setErrors({ reverseReason: t('reverse_reason_required') });
      return;
    }

    const entry = selectedReverseWithdraw;
    const reason = reverseReason.trim();
    const reversedBy = operator();

    // Close right away; the refund and driver notification continue in the background
    handleCloseReverseDialog();

    dispatch(reverseWithdraw(entry, reason, reversedBy)).then(
      () => showToast.success(t('withdrawal_cancelled_successfully')),
      (error) => {
        showToast.error(t('error_cancelling_withdrawal') + ': ' + error.message);
        dispatch(fetchWithdraws());
      }
    );
  };

  // ── Move to the top-up wallet ────────────────────────────────────────
  const handleTransferToTopUp = (rowData) => {
    setSelectedTransferWithdraw(withMobile(rowData));
    setTransferNote('');
  };

  const handleCloseTransferDialog = () => {
    setSelectedTransferWithdraw(null);
    setTransferNote('');
  };

  const handleConfirmTransfer = () => {
    const entry = selectedTransferWithdraw;
    const note = transferNote.trim();
    const transferredBy = operator();

    // Close right away; the top-up credit and driver notification continue in the background
    handleCloseTransferDialog();

    dispatch(transferWithdrawToTopUp(entry, transferredBy, note)).then(
      () => showToast.success(t('withdrawal_transferred_successfully')),
      (error) => {
        showToast.error(t('error_transferring_withdrawal') + ': ' + error.message);
        dispatch(fetchWithdraws());
      }
    );
  };

  // ── Reject (the amount is NOT returned) ──────────────────────────────
  const handleRejectWithdraw = (rowData) => {
    setSelectedRejectWithdraw(withMobile(rowData));
    setRejectReason('');
  };

  const handleCloseRejectDialog = () => {
    setSelectedRejectWithdraw(null);
    setRejectReason('');
  };

  const handleConfirmReject = () => {
    if (!rejectReason.trim()) {
      setErrors({ rejectReason: t('reject_reason_required') });
      return;
    }

    const entry = selectedRejectWithdraw;
    const reason = rejectReason.trim();
    const rejectedBy = operator();

    // Close right away; the record update and driver notification continue in the background
    handleCloseRejectDialog();

    dispatch(rejectWithdraw(entry, reason, rejectedBy)).then(
      () => showToast.success(t('withdrawal_rejected_successfully')),
      (error) => {
        showToast.error(t('error_rejecting_withdrawal') + ': ' + error.message);
        dispatch(fetchWithdraws());
      }
    );
  };

  // ── Table ────────────────────────────────────────────────────────────
  const bankAccountText = (row) => (row.bankAccount ? (allowPrivate ? row.bankAccount : hiddenText) : '');

  const columns = [
    { id: 'date', title: t('requestDate'), field: 'date', type: 'datetime' },
    {
      id: 'name',
      title: t('driver_name'),
      field: 'name',
      nowrap: true,
      render: (rowData) =>
        rowData.name ? (
          <Box component="span" data-no-row-click sx={{ fontWeight: 600 }}>
            <ClickableUserLink value={rowData.name} type="driver" searchField="name" userId={rowData.uid} iconType="car" />
          </Box>
        ) : (
          '-'
        ),
    },
    {
      id: 'amount',
      title: t('amount'),
      field: 'amount',
      type: 'money',
      render: (rowData) => (
        <Box component="span" sx={{ fontWeight: isOpen(rowData) ? 700 : 400 }}>
          {money(rowData.amount)}
        </Box>
      ),
    },
    {
      id: 'status',
      title: t('status'),
      field: 'processed',
      type: 'status',
      status: statusOf,
      sortValue: (row) => statusOf(row).label,
      searchValue: (row) => statusOf(row).label,
    },
    { id: 'processedBy', title: t('processedBy'), field: 'processedBy' },
    { id: 'processDate', title: t('processDate'), field: 'processDate', type: 'datetime' },
    { id: 'paymentSource', title: t('payment_source'), field: 'paymentSource' },
    {
      id: 'referenceCode',
      title: t('reference_code'),
      field: 'referenceCode',
      render: (rowData) =>
        rowData.referenceCode ? (
          <Tooltip title={rowData.referenceCode}>
            <Box
              component="span"
              sx={{ maxWidth: 150, display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}
            >
              {rowData.referenceCode}
            </Box>
          </Tooltip>
        ) : null,
    },
    { id: 'bankName', title: t('bankName'), field: 'bankName', hidden: !showBank, minWidth: 200 },
    { id: 'bankCode', title: t('bankCode'), field: 'bankCode', hidden: !showBank },
    {
      id: 'bankAccount',
      title: t('bankAccount'),
      field: 'bankAccount',
      hidden: !showBank,
      sortValue: (row) => (allowPrivate ? row.bankAccount || '' : ''),
      render: (row) => bankAccountText(row) || null,
    },
    { id: 'id', title: L('id', 'ID'), field: 'id', nowrap: true },
  ];

  // The CSV has the same columns as before (the status in words now, the
  // dates in Addis Ababa time); it is only offered with critical edits on.
  const exportColumns = [
    { title: 'ID', field: 'id' },
    { title: t('requestDate'), field: 'date', type: 'datetime' },
    { title: t('driver_name'), field: 'name' },
    { title: t('amount'), field: 'amount' },
    { title: t('status'), value: (row) => statusOf(row).label },
    { title: t('processedBy'), field: 'processedBy' },
    { title: t('processDate'), field: 'processDate', type: 'datetime' },
    { title: t('payment_source'), field: 'paymentSource' },
    { title: t('reference_code'), field: 'referenceCode' },
    ...(showBank
      ? [
          { title: t('bankName'), field: 'bankName' },
          { title: t('bankCode'), field: 'bankCode' },
          { title: t('bankAccount'), value: (row) => bankAccountText(row) },
        ]
      : []),
  ];

  // Search what staff can see (the account number only with critical edits on).
  const searchFn = (row, q, ql) => {
    const shown = [row.id, row.name, row.amount, row.processedBy, row.paymentSource, row.referenceCode, statusOf(row).label];
    if (showBank) shown.push(row.bankName, row.bankCode, allowPrivate ? row.bankAccount : '');
    return shown.some((v) => v !== undefined && v !== null && String(v).toLowerCase().includes(ql));
  };

  const actions = [
    ...(canProcessWithdrawals
      ? [
          {
            key: 'process',
            label: t('process_withdraw'),
            icon: <TaskAltRoundedIcon />,
            inline: true,
            show: isOpen,
            onClick: handleProcessWithdraw,
          },
        ]
      : []),
    { key: 'details', label: t('view_details'), icon: <ReceiptLongOutlinedIcon />, onClick: handleProcessWithdraw },
    ...(canProcessWithdrawals
      ? [
          { key: 'topup', label: t('transfer_to_topup_wallet'), icon: <AccountBalanceWalletOutlinedIcon />, show: isOpen, onClick: handleTransferToTopUp },
          { key: 'cancel', label: t('cancel_withdraw'), icon: <UndoRoundedIcon />, destructive: true, show: isOpen, onClick: handleReverseWithdraw },
          { key: 'reject', label: t('reject_withdraw'), icon: <BlockRoundedIcon />, destructive: true, show: isOpen, onClick: handleRejectWithdraw },
        ]
      : []),
  ];

  const filterLabel = (key) =>
    key === 'all' ? t('all') : key === 'transferred' ? t('transferred_to_topup') : t(key);
  const filterTone = { pending: 'warning', cancelled: 'error', rejected: 'error', processed: 'success', transferred: 'info' };
  const filterOptions = FILTER_ORDER.map((key) => ({
    value: key,
    label: filterLabel(key),
    count: withdrawdata.withdraws ? counts[key] : undefined,
    dot: filterTone[key] ? tones[filterTone[key]].fg : undefined,
  }));

  const listFailed =
    !withdrawdata.loading &&
    !withdrawdata.withdraws &&
    !!(withdrawdata.error && withdrawdata.error.flag) &&
    withdrawdata.error.msg !== 'No WITHDRAWS available.';

  const countText = ({ shown, total, query, loading }) => {
    if (loading) return L('loading', 'Loading…');
    if (query) return L('table_count_shown', '{{shown}} of {{total}}', { shown, total });
    return t('withdraws_count', {
      count: total,
      defaultValue_one: '{{count}} withdrawal request',
      defaultValue_other: '{{count}} withdrawal requests',
      defaultValue: '{{count}} withdrawal requests',
    });
  };

  const emptyState = ({ query }) => {
    if (query) return null;
    if (statusFilter !== 'all' && all.length > 0) {
      return {
        icon: <FilterAltOffOutlinedIcon />,
        title: L('withdraws_empty_filter', 'No requests with this status'),
        actionLabel: L('withdraws_show_all', 'Show all requests'),
        onAction: () => setStatusFilter('all'),
      };
    }
    return {
      icon: <PaymentsOutlinedIcon />,
      title: L('withdraws_empty', 'No withdrawal requests yet'),
      body: L('withdraws_empty_body', 'Drivers ask to withdraw their earnings in the app. Their requests appear here.'),
    };
  };

  const listTitle = calmCaps(t('Withdraw_title'));

  // ── Dialog pieces ────────────────────────────────────────────────────
  const fieldSx = { mt: 2 };
  const contactRow = (w) =>
    w.mobile ? (
      <InfoRow label={t('contact')}>
        {allowPrivate ? (
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, fontVariantNumeric: 'tabular-nums' }}>
            {formatPhoneDisplay(w.mobile)}
            <CopyButton value={w.mobile} phone what={t('phone', { defaultValue: 'Phone number' })} sx={{ my: -0.5 }} />
          </Box>
        ) : (
          hiddenText
        )}
      </InfoRow>
    ) : null;
  const requestRows = (w, { bank = false, contact = false, date = false } = {}) => (
    <Box component="dl" sx={{ m: 0 }}>
      <InfoRow label={t('driver_name')}>{w.name}</InfoRow>
      <InfoRow label={t('amount')}>
        <Box component="span" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
          {money(w.amount)}
        </Box>
      </InfoRow>
      {bank && w.bankName ? <InfoRow label={t('bankName')}>{w.bankName}</InfoRow> : null}
      {bank && w.bankAccount ? <InfoRow label={t('bankAccount')}>{bankAccountText(w)}</InfoRow> : null}
      {contact ? contactRow(w) : null}
      {date && w.date ? <InfoRow label={t('requestDate')}>{formatAddisDateTime(w.date)}</InfoRow> : null}
    </Box>
  );

  const reasonField = ({ id, label, value, onChange, hint, error, rows = 3 }) => (
    <Box sx={fieldSx}>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <TextField
        id={id}
        fullWidth
        multiline
        minRows={rows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        error={!!error}
        inputProps={{ 'aria-describedby': [hint ? `${id}-hint` : null, error ? `${id}-error` : null].filter(Boolean).join(' ') || undefined }}
      />
      {hint ? <SrOnly id={`${id}-hint`}>{hint}</SrOnly> : null}
      <FieldError id={`${id}-error`}>{error}</FieldError>
    </Box>
  );

  const sw = selectedWithdraw;
  const swOpen = !!sw && isOpen(sw) && canProcessWithdrawals;

  return (
    <Box dir={i18n.dir()} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Paper sx={{ p: { xs: 2, md: 2.5 }, display: 'flex', alignItems: 'center', flexWrap: 'wrap', rowGap: 1.5, columnGap: 3 }}>
        <ChipGroup label={t('status')} value={statusFilter} options={filterOptions} onChange={setStatusFilter} />
      </Paper>

      <DataTable
        title={listTitle}
        rows={data}
        columns={columns}
        loading={!!withdrawdata.loading && !withdrawdata.withdraws}
        refreshing={!!withdrawdata.loading && !!withdrawdata.withdraws}
        error={listFailed ? { title: L('withdraws_load_failed', 'Could not load withdrawal requests.'), onRetry: () => dispatch(fetchWithdraws()) } : null}
        countText={countText}
        searchFn={searchFn}
        resortKey={allowPrivate ? 'private' : 'demo'}
        searchPlaceholder={L('withdraws_search', 'Search driver or reference')}
        defaultSort={{ by: 'date', dir: 'desc' }}
        onRowClick={handleProcessWithdraw}
        rowLabel={(row) => row.name}
        actions={actions}
        prefsKey="withdraws"
        exportCsv={allowPrivate}
        exportColumns={exportColumns}
        exportFileName="withdraws"
        exportTitle={listTitle}
        emptyState={emptyState}
        minWidth={showBank ? 1240 : 960}
      />

      {/* Complete, or the details of a finished request */}
      <MoneyDialog
        fullScreen={phone}
        closeLabel={closeLabel}
        open={!!sw}
        onClose={handleCloseDialog}
        title={swOpen ? t('process_withdraw_title') : t('withdrawal_details')}
        actions={
          swOpen ? (
            <>
              <Button variant="text" color="neutral" onClick={handleCloseDialog}>
                {cancelText}
              </Button>
              <Button variant="contained" startIcon={<TaskAltRoundedIcon />} onClick={handleConfirmProcess}>
                {t('mark_as_completed')}
              </Button>
            </>
          ) : (
            <Button variant="contained" onClick={handleCloseDialog}>
              {calmCaps(t('close'))}
            </Button>
          )
        }
      >
        {sw ? (
          <>
            {requestRows(sw, { bank: true, contact: true })}
            {!isOpen(sw) ? (
              <Box component="dl" sx={{ m: 0, mt: 1.5 }}>
                <InfoRow label={t('status')}>
                  <StatusChip {...statusOf(sw)} />
                </InfoRow>
                {sw.processed ? <InfoRow label={t('processedBy')}>{sw.processedBy}</InfoRow> : null}
                {sw.processed && sw.processDate ? <InfoRow label={t('processDate')}>{formatAddisDateTime(sw.processDate)}</InfoRow> : null}
                {sw.transferredToTopUp && sw.transferNote ? <InfoRow label={t('transfer_note')}>{sw.transferNote}</InfoRow> : null}
                {sw.reversed && sw.reverseReason ? <InfoRow label={t('cancellation_reason')}>{sw.reverseReason}</InfoRow> : null}
                {sw.reversed && sw.reversedBy ? <InfoRow label={t('cancelled_by')}>{sw.reversedBy}</InfoRow> : null}
                {sw.rejected && sw.rejectReason ? <InfoRow label={t('rejection_reason')}>{sw.rejectReason}</InfoRow> : null}
                {sw.rejected && sw.rejectedBy ? <InfoRow label={t('rejected_by')}>{sw.rejectedBy}</InfoRow> : null}
                {sw.rejected && sw.rejectDate ? <InfoRow label={t('processDate')}>{formatAddisDateTime(sw.rejectDate)}</InfoRow> : null}
                {!sw.transferredToTopUp && sw.paymentSource ? <InfoRow label={t('payment_source')}>{sw.paymentSource}</InfoRow> : null}
                {!sw.transferredToTopUp && sw.referenceCode ? <InfoRow label={t('reference_code')}>{sw.referenceCode}</InfoRow> : null}
              </Box>
            ) : null}
            {swOpen ? (
              <>
                <Box sx={fieldSx}>
                  <FieldLabel id="withdraw-source-label">{t('payment_source')}</FieldLabel>
                  <TextField
                    select
                    fullWidth
                    value={paymentSource}
                    error={!!errors.paymentSource}
                    onChange={(e) => {
                      setPaymentSource(e.target.value);
                      if (e.target.value !== 'Other') {
                        setOtherSource('');
                      }
                    }}
                    SelectProps={{ labelId: 'withdraw-source-label', SelectDisplayProps: { 'aria-describedby': errors.paymentSource ? 'withdraw-source-error' : undefined } }}
                  >
                    {PAYMENT_SOURCES.map((s) => (
                      <MenuItem key={s} value={s}>
                        {s === 'Other' ? t('other') : s}
                      </MenuItem>
                    ))}
                  </TextField>
                  <FieldError id="withdraw-source-error">{errors.paymentSource}</FieldError>
                </Box>
                {paymentSource === 'Other' ? (
                  <Box sx={fieldSx}>
                    <FieldLabel htmlFor="withdraw-other-source">{t('specify_source')}</FieldLabel>
                    <TextField
                      id="withdraw-other-source"
                      fullWidth
                      value={otherSource}
                      error={!!errors.otherSource}
                      onChange={(e) => setOtherSource(e.target.value)}
                      inputProps={{ 'aria-describedby': errors.otherSource ? 'withdraw-other-source-error' : undefined }}
                    />
                    <FieldError id="withdraw-other-source-error">{errors.otherSource}</FieldError>
                  </Box>
                ) : null}
                <Box sx={fieldSx}>
                  <FieldLabel htmlFor="withdraw-reference">{t('reference_code')}</FieldLabel>
                  <TextField
                    id="withdraw-reference"
                    fullWidth
                    value={referenceCode}
                    error={!!errors.referenceCode}
                    onChange={(e) => setReferenceCode(e.target.value)}
                    inputProps={{
                      autoComplete: 'off',
                      'aria-describedby': ['withdraw-reference-hint', errors.referenceCode ? 'withdraw-reference-error' : null].filter(Boolean).join(' '),
                    }}
                  />
                  <SrOnly id="withdraw-reference-hint">{t('reference_code_help')}</SrOnly>
                  <FieldError id="withdraw-reference-error">{errors.referenceCode}</FieldError>
                </Box>
              </>
            ) : null}
          </>
        ) : null}
      </MoneyDialog>

      {/* Cancel: the amount goes back to the driver's earning wallet */}
      <MoneyDialog
        fullScreen={phone}
        closeLabel={closeLabel}
        open={!!selectedReverseWithdraw}
        onClose={handleCloseReverseDialog}
        title={t('cancel_withdraw_title')}
        actions={
          <>
            <Button variant="text" color="neutral" onClick={handleCloseReverseDialog}>
              {cancelText}
            </Button>
            <Button variant="contained" color="error" onClick={handleConfirmReverse}>
              {t('confirm_cancellation')}
            </Button>
          </>
        }
      >
        {selectedReverseWithdraw ? (
          <>
            <InlineMessage kind="warning" sx={{ mb: 1.5 }}>
              {t('cancel_withdraw_warning')}
            </InlineMessage>
            {requestRows(selectedReverseWithdraw, { bank: true, date: true })}
            {reasonField({
              id: 'withdraw-reverse-reason',
              label: t('cancellation_reason'),
              value: reverseReason,
              onChange: setReverseReason,
              hint: `${t('enter_cancellation_reason')} ${t('cancellation_reason_help')}`,
              error: errors.reverseReason,
            })}
          </>
        ) : null}
      </MoneyDialog>

      {/* Move to the top-up wallet instead of paying out */}
      <MoneyDialog
        fullScreen={phone}
        closeLabel={closeLabel}
        open={!!selectedTransferWithdraw}
        onClose={handleCloseTransferDialog}
        title={t('transfer_to_topup_title')}
        actions={
          <>
            <Button variant="text" color="neutral" onClick={handleCloseTransferDialog}>
              {cancelText}
            </Button>
            <Button variant="contained" startIcon={<AccountBalanceWalletOutlinedIcon />} onClick={handleConfirmTransfer}>
              {t('confirm_transfer')}
            </Button>
          </>
        }
      >
        {selectedTransferWithdraw ? (
          <>
            <InlineMessage kind="info" sx={{ mb: 1.5 }}>
              {t('transfer_to_topup_warning')}
            </InlineMessage>
            {requestRows(selectedTransferWithdraw, { contact: true, date: true })}
            {reasonField({
              id: 'withdraw-transfer-note',
              label: t('transfer_note'),
              value: transferNote,
              onChange: setTransferNote,
              hint: `${t('enter_transfer_note')} ${t('transfer_note_help')}`,
            })}
          </>
        ) : null}
      </MoneyDialog>

      {/* Reject: the amount is NOT returned */}
      <MoneyDialog
        fullScreen={phone}
        closeLabel={closeLabel}
        open={!!selectedRejectWithdraw}
        onClose={handleCloseRejectDialog}
        title={t('reject_withdraw_title')}
        actions={
          <>
            <Button variant="text" color="neutral" onClick={handleCloseRejectDialog}>
              {cancelText}
            </Button>
            <Button variant="contained" color="error" onClick={handleConfirmReject}>
              {t('confirm_rejection')}
            </Button>
          </>
        }
      >
        {selectedRejectWithdraw ? (
          <>
            <InlineMessage kind="error" sx={{ mb: 1.5 }}>
              {t('reject_withdraw_warning')}
            </InlineMessage>
            {requestRows(selectedRejectWithdraw, { contact: true, date: true })}
            {reasonField({
              id: 'withdraw-reject-reason',
              label: t('rejection_reason'),
              value: rejectReason,
              onChange: setRejectReason,
              hint: `${t('enter_rejection_reason')} ${t('rejection_reason_help')}`,
              error: errors.rejectReason,
            })}
          </>
        ) : null}
      </MoneyDialog>
    </Box>
  );
};

export default Withdraws;
